import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import useDocumentTitle from '../../UseDocumentTitle';
import { pricingTiers, Currency, Interval, TRIAL_DAYS, isPaidTier } from '../../Data/PricingData';
import { useCheckout, useCurrency } from '../../hooks/useCheckout';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Tier } from '../../types/user';
import SpaceShowcase from './StudySpacesShowcase';
import QuizShowcase from './QuizShowcase';
import ImageGenShowcase from './ImageGenShowcase';
import FaqSection from './FaqSection';
import PricingCard from './PricingCard';
import BillingToggle from './BillingToggle';
import PlanComparison from './PlanComparison';
import ReturnToBuddy from './ReturnToBuddy';
import CurrencySelect from './CurrencySelect';
import '../../Styles/Pricing/Pricing.css';

const asInterval = (v: string | null): Interval | null => (v === 'monthly' || v === 'annual' ? v : null);
const asCurrency = (v: string | null): Currency | null => (v === 'usd' || v === 'cad' || v === 'eur' ? v : null);

const Pricing = () => {
    useDocumentTitle('StudyBuddy - Pricing');
    const { isAuthenticated, isLoading, isDataLoading, isSubscriptionLoading, subscription } = useAuth();
    const { tierName } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { trackPricingViewed } = useAnalytics();

    // Read the resume-checkout params once; they are stripped from the URL after use.
    const [initialParams] = useState(() => {
        const params = new URLSearchParams(location.search);
        return {
            tier: (params.get('tier') || tierName || '').toLowerCase(),
            interval: asInterval(params.get('interval')),
            currency: asCurrency(params.get('currency')),
            promo: params.get('promo') || undefined,
            ref: params.get('ref') || undefined,
        };
    });

    const [billingCycle, setBillingCycle] = useState<Interval>(initialParams.interval ?? 'monthly');
    const [currency, setCurrency] = useCurrency(initialParams.currency);
    const { startCheckout, pendingTier } = useCheckout('pricing_page');
    const cardsRef = useRef<HTMLDivElement>(null);
    const resumeHandled = useRef(false);

    const authReady = !isLoading && !isDataLoading && !isSubscriptionLoading;
    const currentPlan: Tier | null = !authReady || !isAuthenticated ? null : (subscription?.plan ?? 'free');

    useEffect(() => {
        trackPricingViewed({ source: initialParams.ref ?? (location.hash.slice(1) || 'direct') });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // After sign-in we land back here with ?tier=…; continue straight to checkout, exactly once.
    useEffect(() => {
        if (resumeHandled.current || !authReady) return;
        if (!isPaidTier(initialParams.tier)) return;
        resumeHandled.current = true;
        navigate({ pathname: '/pricing', hash: location.hash }, { replace: true });
        if (!isAuthenticated) return;
        startCheckout({
            tier: initialParams.tier,
            interval: initialParams.interval ?? billingCycle,
            currency: initialParams.currency ?? currency,
            promoCode: initialParams.promo,
        });
    }, [authReady, isAuthenticated, initialParams, billingCycle, currency, startCheckout, navigate, location.hash]);

    // Deep links such as /pricing#study-spaces
    useEffect(() => {
        if (!location.hash) return;
        const el = document.getElementById(location.hash.slice(1));
        if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }, [location.hash]);

    const handleSelect = useCallback((tier: Tier) => {
        startCheckout({ tier, interval: billingCycle, currency, promoCode: initialParams.promo });
    }, [startCheckout, billingCycle, currency, initialParams.promo]);

    const scrollToPlans = useCallback(() => {
        cardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, []);

    return (
        <div className="pricing-wrapper">
            <Helmet>
                <title>StudyBuddy Pricing - Choose Your Plan</title>
                <meta name="description" content={`Compare StudyBuddy's Free, Premium and Ultimate plans. Paid plans start with a ${TRIAL_DAYS}-day free trial and can be cancelled anytime.`} />
                <link rel="canonical" href="https://studdybuddy.ca/pricing" />
                <meta name="robots" content="index, follow" />
            </Helmet>

            <ReturnToBuddy />

            <div className="pricing-container">
                <header className="pricing-hero">
                    <p className="pricing-eyebrow">Pricing</p>
                    <h1>Pick the Plan That Fits How You Work</h1>
                    <div className="pricing-controls">
                        <BillingToggle billingCycle={billingCycle} setBillingCycle={setBillingCycle} currency={currency} />
                        <CurrencySelect currency={currency} onChange={setCurrency} />
                    </div>
                </header>

                <div className="pricing-cards" ref={cardsRef}>
                    {pricingTiers.map(tier => (
                        <PricingCard
                            key={tier.idname}
                            tier={tier}
                            billingCycle={billingCycle}
                            currency={currency}
                            currentPlan={currentPlan}
                            isTrialing={subscription?.status === 'trialing'}
                            pending={pendingTier === tier.idname}
                            disabled={pendingTier !== null && pendingTier !== tier.idname}
                            onSelect={handleSelect}
                        />
                    ))}
                </div>

                <PlanComparison />

                <div className="pricing-showcases">
                    <ImageGenShowcase />
                    <QuizShowcase />
                    <SpaceShowcase scrollToPricing={scrollToPlans} />
                </div>

                <FaqSection />

                {!isPaidTier(currentPlan) && (
                <section className="pricing-final-cta" aria-labelledby="pricing-final-cta-heading">
                    <h2 id="pricing-final-cta-heading">Try Ultimate Free for {TRIAL_DAYS} Days</h2>
                    <p>Every feature, the highest limits, and Spaces for your projects. Cancel before the trial ends and you won't be charged.</p>
                    <button type="button" className="pricing-button is-primary" onClick={scrollToPlans}>
                        Choose a Plan
                    </button>
                </section>
                )}
            </div>
        </div>
    );
};

export default Pricing;
